<script lang="ts">
import {onMounted, defineComponent, ref, toRefs, inject} from 'vue';
import {driver} from 'driver.js';
import {introConfig} from './introConfig.ts';
import {getStepById} from './utils.ts';
import {dashboardConfig} from './dashboardConfig.ts';
import 'driver.js/dist/driver.css';
import type {Container} from '../../container.ts';

export default defineComponent({
  name: 'AllSpiceOnboarding',
  props: {
    onboardingProgress: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const onboardingTrigger = ref(null);
    const {onboardingProgress} = toRefs(props);
    const container: Container = inject('container');
    const driverObj = driver();

    function formatProgress() {
      const progress = JSON.parse(onboardingProgress.value);
      const sectionKeys = Object.keys(progress?.sections);

      for (const key of sectionKeys) {
        const currentSection = progress.sections[key];
        const completedSteps = currentSection.steps_completed;
        if (key === 'dashboard') {
          currentSection.completed =
            completedSteps === dashboardConfig(driverObj, introConfig, {}, container).steps.length - 1;
        }
      }
      return progress;
    }

    const onboardingProgressParsed = formatProgress();

    function triggerOnboarding() {
      driverObj.setConfig(introConfig(driverObj, onboardingProgressParsed, container));
      driverObj.drive();
    }

    function hasCompletedAllSections() {
      const progressObject = Object.values(onboardingProgressParsed);

      for (const item of progressObject) {
        for (const value of Object.values(item)) {
          if (!value.completed && !value.skipped) {
            return false;
          }
        }
      }
      return true;
    }

    function isOnboardingActive() {
      // Only run on home page for signed in users and the url doesn't
      // contain the 'onboarding' param
      const queryString = window.location.search;
      const queryParameters = new URLSearchParams(queryString);
      const isActive =
        !onboardingProgressParsed.sections.intro.skipped &&
        !hasCompletedAllSections() &&
        !queryParameters.has('onboarding') &&
        window.location.href === window.config.appUrl;
      return isActive;
    }

    onMounted(() => {
      // add event listener to parent to capture Enter event for accessibility purposes
      onboardingTrigger.value.parentElement.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        triggerOnboarding();
      });

      const queryString = window.location.search;
      const queryParameters = new URLSearchParams(queryString);
      const onboardingParam = queryParameters.get('onboarding');
      // triggers if the user tries to start onboarding from a different page
      if (onboardingParam === 'dashboard') {
        driverObj.setConfig(
          dashboardConfig(driverObj, introConfig, onboardingProgressParsed, container),
        );
        driverObj.drive();
      }

      if (onboardingParam === 'migration-cards') {
        driverObj.setConfig(
          dashboardConfig(driverObj, introConfig, onboardingProgressParsed, container),
        );

        const stepIdx = getStepById(driverObj, 'migrate-card');
        driverObj.drive(stepIdx);
      }

      if (onboardingParam === 'migration-form') {
        driverObj.setConfig(
          dashboardConfig(driverObj, introConfig, onboardingProgressParsed, container),
        );

        const stepIdx = getStepById(driverObj, 'migrate-url-input');
        driverObj.drive(stepIdx);
      }

      if (isOnboardingActive()) {
        triggerOnboarding();
      }
    });

    return {
      triggerOnboarding,
      onboardingTrigger,
    };
  },
});
</script>

<template>
  <div
    ref="onboardingTrigger"
    class="onboarding-trigger-btn"
    @click="triggerOnboarding"
  >
    <!-- project-roadmap icon source: https://primer.style/design/foundations/icons/project-roadmap-16 -->
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      width="16"
      height="16"
    >
      <path
        fill="currentColor"
        d="M4.75 7a.75.75 0 0 0 0 1.5h4.5a.75.75 0 0 0 0-1.5h-4.5ZM5 4.75A.75.75 0 0 1 5.75 4h5.5a.75.75 0 0 1 0 1.5h-5.5A.75.75 0 0 1 5 4.75ZM6.75 10a.75.75 0 0 0 0 1.5h4.5a.75.75 0 0 0 0-1.5h-4.5Z"
      />
      <path
        fill="currentColor"
        d="M0 1.75C0 .784.784 0 1.75 0h12.5C15.216 0 16 .784 16 1.75v12.5A1.75 1.75 0 0 1 14.25 16H1.75A1.75 1.75 0 0 1 0 14.25Zm1.75-.25a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25V1.75a.25.25 0 0 0-.25-.25Z"
      />
    </svg>
    Onboarding
  </div>
</template>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style>
.ui.menu .ui.dropdown .menu > .item.item-vue-component {
  padding: 0 !important;
}

.onboarding-trigger-btn {
  background: none;
  border: none;
  color: var(--color-caret);
  display: flex;
  max-height: 38px;
  pointer-events: none;
  text-align: left;
  width: 100%;

  &:hover,
  &:focus-visible {
    background: var(--color-hover);
    color: var(--color-text);
  }

  & svg {
    margin-right: 0.78rem;
  }
}

.driver-popover {
  background-color: var(--color-body);
  max-width: 360px;
}

.intro-popover {
  max-height: 70%;
  max-width: 500px;
  overflow-y: auto;
}

.onboarding-checklist {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  list-style: none;
  padding-left: 0;

  & li {
    display: flex;
    gap: 10px;
  }

  & .status-container {
    padding: 8px;
    line-height: 0;
    background: white;
  }

  & button {
    align-items: center;
    background: #0f375f;
    border-radius: 6px;
    border: 1px solid #0f375f;
    display: flex;
    flex-direction: row;
    gap: 10px;
    overflow: hidden;
    padding: 0 8px 0 0;
    position: relative;
    text-align: left;
    width: 100%;

    & span {
      color: var(--color-white);
    }

    &:hover,
    &:focus-visible {
      background-color: #1a5795;
      border-color: #1a5795;
    }
  }
}

.driver-popover-close-btn {
  color: var(--color-text);

  &:hover,
  &:focus,
  &:focus-visible {
    color: #1a5795;
  }
}

.driver-popover-progress-text,
.driver-popover-title,
.driver-popover-description {
  color: var(--color-text);
}

.driver-popover-progress-text {
  width: 100%;
}

.driver-popover-description {
  & h2 {
    margin-top: 10px;
    color: var(--color-text);
  }
}

.driver-popover-footer {
  flex-wrap: wrap;
}

.driver-popover-arrow {
  border-right-color: var(--color-body);
}

.driver-popover-footer
  button:where(
    .driver-popover-next-btn,
    .driver-popover-prev-btn,
    .driver-popover-skip-btn,
    button
  ) {
  background-color: var(--color-body);
  border: 1px solid var(--color-hover);
  color: var(--color-text);
  padding: 6px 12px;
  text-shadow: none;

  &:hover,
  &:focus,
  &:focus-visible {
    background-color: var(--color-hover);
  }
}

.driver-popover-navigation-btns {
  align-items: baseline;

  & button:first-of-type {
    margin-right: auto;
    margin-top: 16px;
  }
}

.completion-date {
  align-self: center;
  font-size: 12px;
  margin-left: auto;
}

/* overwrite package css to allow navigation menu to be visible during onboarding */
:not(body):has(> .driver-active-element) {
  overflow: visible !important;
}
</style>
